import React from "react";
import MuiCard from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import type { CardProps as MuiCardProps } from "@mui/material/Card";
import type { BaseMoleculeProps, ComponentVariant } from "../../types";

export interface CardProps
  extends Omit<MuiCardProps, "variant">,
    Omit<BaseMoleculeProps, "variant"> {
  /**
   * Card elevation level (0-24)
   */
  elevation?: number;

  /**
   * Card variant with mobile-first considerations
   */
  variant?: ComponentVariant | "outlined";

  /**
   * Card header content
   */
  header?: React.ReactNode;

  /**
   * Card footer/actions content
   */
  actions?: React.ReactNode;

  /**
   * Padding variant for mobile-first design
   */
  padding?: "none" | "small" | "medium" | "large";

  /**
   * Loading state
   */
  loading?: boolean;

  /**
   * Click handler for interactive cards
   */
  onClick?: () => void;

  /**
   * Hover effect enabled
   */
  hoverable?: boolean;
}

const CardComponent: React.FC<CardProps> = ({
  elevation = 2,
  variant = "default",
  header,
  actions,
  padding = "medium",
  loading = false,
  onClick,
  hoverable = false,
  className,
  "data-testid": _dataTestId,
  children,
  ...props
}) => {
  const paddingMap = {
    none: 0,
    small: { xs: 1, x2l: 1.5 },
    medium: { xs: 2, x2l: 3 },
    large: { xs: 3, x2l: 4 },
  };

  const cardProps: MuiCardProps = {
    elevation: variant === "outlined" ? 0 : elevation,
    variant: variant === "outlined" ? "outlined" : "elevation",
    onClick,
    className,
    sx: {
      // Mobile-first responsive styling
      borderRadius: {
        xs: "8px", // Mobile - smaller radius
        x2l: "12px", // Desktop - larger radius
      },
      // Padding based on variant
      "& .MuiCardContent-root": {
        padding: {
          xs:
            typeof paddingMap[padding] === "object"
              ? `${paddingMap[padding].xs}rem`
              : `${paddingMap[padding]}rem`,
          x2l:
            typeof paddingMap[padding] === "object"
              ? `${paddingMap[padding].x2l}rem`
              : `${paddingMap[padding]}rem`,
        },
        "&:last-child": {
          paddingBottom: {
            xs:
              typeof paddingMap[padding] === "object"
                ? `${paddingMap[padding].xs}rem`
                : `${paddingMap[padding]}rem`,
            x2l:
              typeof paddingMap[padding] === "object"
                ? `${paddingMap[padding].x2l}rem`
                : `${paddingMap[padding]}rem`,
          },
        },
      },
      // Compact variant
      ...(variant === "compact" && {
        "& .MuiCardContent-root": {
          padding: { xs: "8px", x2l: "12px" },
          "&:last-child": { paddingBottom: { xs: "8px", x2l: "12px" } },
        },
      }),
      // Expanded variant
      ...(variant === "expanded" && {
        "& .MuiCardContent-root": {
          padding: { xs: "24px", x2l: "32px" },
          "&:last-child": { paddingBottom: { xs: "24px", x2l: "32px" } },
        },
      }),
      // Hover effect
      ...(hoverable && {
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: (theme) => theme.shadows[elevation + 2],
        },
      }),
      // Loading state
      ...(loading && {
        opacity: 0.7,
        pointerEvents: "none",
      }),
    },
    ...props,
  };

  return (
    <MuiCard {...cardProps}>
      {header}
      <CardContent>{children}</CardContent>
      {actions && (
        <CardActions
          sx={{
            padding: {
              xs: "8px 16px",
              x2l: "12px 24px",
            },
          }}
        >
          {actions}
        </CardActions>
      )}
    </MuiCard>
  );
};

// Apply memoization with React.memo
export const Card = React.memo(CardComponent);

// Set display name for debugging
(Card as any).displayName = "Card";
